import { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import { colors, screen } from 'components/styles/variables';
import { useTranslation } from 'react-i18next';
import { trackDealerPagesClick } from 'lib/Tracking/DealerPages';
import { DealerPagesEventType } from 'lib/EventSchema';
import TrackingDataContext from 'contexts/TrackingDataContext';

export const DEFAULT_NUM_OF_DEALERS = 5;

function Button({ onClick, expanded, className }) {
  const { t } = useTranslation();

  return (
    <button
      onClick={() => onClick()}
      data-testid="button"
      className={className}
    >
      {expanded ? t('home.showLess') : t('home.showMore')}
      <style jsx>{`
        button {
          font-size: 16px;
          font-weight: bold;
          color: ${colors.ALR1};
          border: none;
          background: transparent;
          align-self: center;
        }

        @media screen and (min-width: ${screen.smMin}) {
          button {
            display: none;
          }
        }
      `}</style>
    </button>
  );
}

Button.propTypes = {
  onClick: PropTypes.func.isRequired,
  expanded: PropTypes.bool.isRequired,
  className: PropTypes.string,
};

function NearbyDealerLink({ title, url }) {
  const { dealerPageUrl } = useContext(TrackingDataContext);

  function onClick() {
    trackDealerPagesClick({
      type: DealerPagesEventType.nearby_dealership_click,
      dealerPageUrl,
      targetPageUrl: `/dealers/${url}`,
    });
  }

  return (
    <div>
      <Link href={`/dealers/${url}`} legacyBehavior>
        <a data-testid="nearby-dealer-link" onClick={onClick}>
          {title}
        </a>
      </Link>
      <style jsx>{`
        a {
          font-size: 14px;
          font-weight: 600;
          color: ${colors.BLACK};
        }
      `}</style>
    </div>
  );
}

NearbyDealerLink.propTypes = {
  title: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
};

export default function NearbyDealers({ nearbyDealers, topRef }) {
  const [isExpanded, setIsExpanded] = useState(false);

  function toggleExpansion() {
    if (isExpanded) {
      window.scrollTo(0, topRef.current.offsetTop);
    }
    setIsExpanded(!isExpanded);
  }

  function shouldHideDealer(index) {
    return index > DEFAULT_NUM_OF_DEALERS - 1 && !isExpanded;
  }

  return (
    <div className="nearby-dealers">
      <ul data-testid="dealer-list">
        {nearbyDealers.map(({ name, dealerPageUrl }, index) => (
          <li
            key={dealerPageUrl}
            className={shouldHideDealer(index) ? 'hidden-xs' : null}
          >
            <NearbyDealerLink title={name || ''} url={dealerPageUrl} />
          </li>
        ))}
      </ul>
      {nearbyDealers.length > DEFAULT_NUM_OF_DEALERS && (
        <Button expanded={isExpanded} onClick={toggleExpansion} />
      )}

      <style jsx>{`
        .nearby-dealers {
          width: 100%;
          display: flex;
          flex-direction: column;
          padding-bottom: 55px;
        }

        ul {
          padding-left: 53px;
          list-style: none;
        }

        li {
          padding: 10px 0px;
        }

        @media (min-width: ${screen.smMin}) {
          ul {
            width: 100%;
            display: flex;
            flex-wrap: wrap;
          }
          li {
            min-width: 50%;
            max-width: 50%;
          }
        }

        @media (min-width: ${screen.mdMin}) {
          li {
            min-width: 33%;
            max-width: 33%;
          }
        }
      `}</style>
    </div>
  );
}

NearbyDealers.propTypes = {
  nearbyDealers: PropTypes.arrayOf(PropTypes.object).isRequired,
  topRef: PropTypes.object.isRequired,
};
